import { matchPath, useLocation, useParams } from 'react-router-dom';

import { AppRouteKey, PlatformRoleEnum, PersonaRoleEnum } from './route_types';
import { AppRoutes } from './routes';

export type AppRouteKeys = keyof typeof AppRoutes;

// Get route path with optional params for dynamic routes
export const getRoutePath = (route: AppRouteKey, params?: Record<string, string>): string => {
  const basePath = AppRoutes[route].path;
  if (!params) return basePath;

  // Simple placeholder replacement for dynamic routes
  let path = basePath;
  Object.entries(params).forEach(([key, value]) => {
    path = path.replace(`:${key}`, value);
  });

  return path;
};

export const getRouteKeyFromPath = (path: string): AppRouteKey => {
  // Check for exact path matches
  for (const [key, route] of Object.entries(AppRoutes)) {
    if (route.path === path) {
      return key as AppRouteKey;
    }
  }

  // Handle dynamic routes
  for (const [key, route] of Object.entries(AppRoutes)) {
    if (route.path.includes(':')) {
      const pathPattern = route.path.replace(/:[^/]+/g, '[^/]+');
      const regex = new RegExp(`^${pathPattern}$`);

      if (regex.test(path)) {
        return key as AppRouteKey;
      }
    }
  }

  return AppRouteKey.HOME;
};

export const getRouteName = (route: AppRouteKey): string => AppRoutes[route].name;

export const getRouteFromPath = (path: string): AppRouteKey | undefined => {
  for (const [key, info] of Object.entries(AppRoutes)) {
    const routePath = info.path;

    // Handle wildcard route (*)
    if (routePath === '*') {
      return key as AppRouteKey;
    }

    // Convert route path with :params into regex
    const regex = new RegExp(
      '^' +
        routePath
          .replace(/:[^/]+/g, '[^/]+') // replace :param with segment matcher
          .replaceAll('*', '.*') + // handle wildcards
        '$'
    );

    if (regex.test(path)) {
      return key as AppRouteKey;
    }
  }

  return undefined;
};

// Get the parent route key of a route
export const getParentRoute = (route: AppRouteKey): AppRouteKey | undefined => {
  const parentKey = AppRoutes[route].parent;
  return parentKey ? (parentKey as AppRouteKey) : undefined;
};

// Check if a route is a parent of another route
export const isParentOf = (parentRoute: AppRouteKey, childRoute: AppRouteKey): boolean => {
  let current: AppRouteKey | undefined = childRoute;

  while (current) {
    if (current === parentRoute) return true;

    const parentKey = AppRoutes[current].parent;
    if (!parentKey) break;

    current = parentKey as AppRouteKey;
  }

  return false;
};

// Helper for path matching to determine if path belongs to route hierarchy
export const isPathInRouteHierarchy = (basePath: string, pathToCheck: string): boolean => {
  // Remove trailing slashes for consistent comparison
  const normalizedBase = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;
  const normalizedPath = pathToCheck.endsWith('/') ? pathToCheck.slice(0, -1) : pathToCheck;

  // Check if the path is exactly the base or starts with base followed by /
  return normalizedPath === normalizedBase || normalizedPath.startsWith(normalizedBase + '/');
};

// Find all routes that are children of a given route
export const findChildRoutes = (parentRoute: AppRouteKey): AppRouteKey[] => {
  return Object.entries(AppRoutes)
    .filter(([_, info]) => info.parent === parentRoute)
    .map(([key]) => key as AppRouteKey);
};

// Helper function to normalize string to enum
export function normalizePlatformRole(role: string | undefined): PlatformRoleEnum | undefined {
  if (!role) return undefined;
  return PlatformRoleEnum[role.toUpperCase() as keyof typeof PlatformRoleEnum];
}

// Helper function to normalize string to enum
export function normalizePersonaRole(role: string | undefined): PersonaRoleEnum | undefined {
  // replace all spaces with underscores
  const normalizedRole = role?.replace(/\s+/g, '_').toUpperCase();
  if (!normalizedRole) return undefined;
  return PersonaRoleEnum[normalizedRole as keyof typeof PersonaRoleEnum];
}

export interface BreadcrumbItem {
  name: string;
  nameKey?: string;
  path?: string;
  isDynamicName?: boolean;
}

export function useBreadcrumbs(loaderData?: any): BreadcrumbItem[] {
  const { pathname } = useLocation();
  const params = useParams();

  const breadcrumbParts = Object.values(AppRoutes)
    .filter((route) => matchPath({ path: route.path, end: false }, pathname))
    .map((route) => {
      // Generate the actual path by replacing params
      let actualPath = route.path;
      Object.entries(params).forEach(([key, value]) => {
        if (value) {
          actualPath = actualPath.replace(`:${key}`, value).replace(`:${key}?`, value);
        }
      });

      // Use getName function if available, otherwise use static name
      const hasDynamicName = !!route.getName;
      const dynamicName = hasDynamicName ? route.getName!({ params, loaderData }) : route.name;

      return {
        name: dynamicName,
        nameKey: route.nameKey,
        path: actualPath,
        isDynamicName: hasDynamicName,
      };
    });

  return breadcrumbParts.length > 0
    ? breadcrumbParts
    : [{ name: AppRoutes.HOME.name, nameKey: AppRoutes.HOME.nameKey, path: AppRoutes.HOME.path }];
}

export function useFarmerReportBreadcrumbs(loaderData?: any, persistParams: string[] = []) {
  const { pathname, search } = useLocation();
  const params = useParams();

  // Parse query params into an object
  const urlSearchParams = new URLSearchParams(search);
  const persistedQuery = new URLSearchParams();

  persistParams.forEach((key) => {
    const value = urlSearchParams.get(key);
    if (value) persistedQuery.set(key, value);
  });

  const matchedRoutes = Object.values(AppRoutes).filter((route) =>
    matchPath({ path: route.path, end: false }, pathname)
  );

  // Check if user is Regional Manager
  const isRegionalManager = loaderData?.userMetadata?.organizations
    ?.find((org: any) => org.isCurrent)
    ?.personaRoles?.some(
      (role: any) =>
        role.personaRoleName?.toLowerCase() === PersonaRoleEnum.REGIONAL_MANAGER.toLowerCase()
    );

  // Determine which routes to skip based on geostructure (only for Regional Managers)
  const defaultGeostructureList = loaderData?.defaultGeostructureList;
  const startingAddressPartName = defaultGeostructureList?.[0]?.addressPartName;

  // Define the route hierarchy and where each addressPartName should start
  const getSkipRoutesBasedOnGeostructure = (
    addressPartName: string | undefined
  ): Set<AppRouteKey> => {
    const skipRoutes = new Set<AppRouteKey>();

    // Only apply skip logic for Regional Managers
    if (!addressPartName || !isRegionalManager) return skipRoutes;

    // Based on the routeFarmerReportPageHandler logic:
    switch (addressPartName) {
      case 'region':
      case 'region_part':
        // Start from /farmer-report - no routes to skip
        break;
      case 'settlement':
        // Start from /farmer-report/division-report/undefined
        // Skip FARMER_REPORT root
        skipRoutes.add(AppRouteKey.FARMER_REPORT);
        break;
      case 'settlement_part':
        // Start from /farmer-report/zone-report/undefined
        // Skip FARMER_REPORT and DIVISION_REPORT
        skipRoutes.add(AppRouteKey.FARMER_REPORT);
        skipRoutes.add(AppRouteKey.FARMER_REPORT_DIVISION_REPORT);
        break;
      case 'custom_geography_structure_1':
        // Start from /farmer-report/division-report/undefined/municipality-report/undefined
        // Skip FARMER_REPORT and DIVISION_REPORT
        skipRoutes.add(AppRouteKey.FARMER_REPORT);
        skipRoutes.add(AppRouteKey.FARMER_REPORT_DIVISION_REPORT);
        break;
      default:
        // Default: start from /farmer-report
        break;
    }

    return skipRoutes;
  };

  const skipRoutes = getSkipRoutesBasedOnGeostructure(startingAddressPartName);

  const breadcrumbParts = matchedRoutes
    .filter((route) => !skipRoutes.has(route.key as AppRouteKey))
    .map((route) => {
      const label = route.getName ? route.getName({ params, loaderData }) : route.name;

      // Replace dynamic :params in route path
      let path = route.path;
      Object.entries(params).forEach(([key, val]) => {
        path = path.replace(`:${key}`, String(val));
      });

      // Append selected query params only
      const queryString = persistedQuery.toString();
      const fullPath = queryString ? `${path}?${queryString}` : path;

      const staticLabel = route.staticLabel;
      return { label, path: fullPath, staticLabel };
    });

  return breadcrumbParts.length > 0
    ? breadcrumbParts
    : [{ label: AppRoutes.HOME.name, path: '/', staticLabel: '' }];
}
